import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { Upload, CheckCircle2, AlertCircle, FileText, Calendar, Clock } from "lucide-react";
import { MoneyInput } from "@/components/MoneyInput";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const PUBLIC_API = axios.create({
    baseURL: `${BACKEND}/api/public`,
    headers: { "Content-Type": "application/json" },
});

function formatBytes(n) {
    if (!n || n <= 0) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function timeUntil(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        const ms = d.getTime() - Date.now();
        if (ms <= 0) return "expired";
        const hr = Math.floor(ms / 3_600_000);
        if (hr >= 24) return `${Math.floor(hr / 24)}d ${hr % 24}h remaining`;
        if (hr > 0) return `${hr}h remaining`;
        return `${Math.floor(ms / 60_000)}m remaining`;
    } catch {
        return "";
    }
}

export default function PublicUpload() {
    const { token } = useParams();
    const [info, setInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const { data } = await PUBLIC_API.get(`/upload-token/${token}`);
                setInfo(data);
            } catch (e) {
                setError(
                    e.response?.data?.detail ||
                    "This upload link is invalid or has expired."
                );
            } finally {
                setLoading(false);
            }
        })();
    }, [token]);

    const refreshInfo = useCallback(async () => {
        try {
            const { data } = await PUBLIC_API.get(`/upload-token/${token}`);
            setInfo(data);
        } catch (err) {
            // Refresh is best-effort; existing state stays. Log so issues surface in devtools.
            console.warn("Upload link refresh failed:", err);
        }
    }, [token]);

    if (loading) {
        return (
            <CenterShell>
                <div className="text-ink/50 text-sm">Validating link…</div>
            </CenterShell>
        );
    }
    if (error) {
        return (
            <CenterShell>
                <div className="bg-white border border-red-200 p-10 max-w-md text-center" data-testid="upload-error">
                    <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" strokeWidth={1.5} />
                    <h1 className="font-display font-bold text-2xl text-ink mb-2">Link unavailable</h1>
                    <p className="text-sm text-ink/60">{error}</p>
                    <p className="mt-4 text-xs text-ink/40">Please contact your HR administrator for a new link.</p>
                </div>
            </CenterShell>
        );
    }

    const accent = info.primary_color || "#002FA7";
    const allDone = (info.doc_types || []).every((t) => t.uploaded);

    return (
        <div className="min-h-screen bg-bone py-8 px-4 md:px-8" data-testid="public-upload-page">
            <div className="max-w-3xl mx-auto">
                <header className="mb-8 flex items-center gap-4 flex-wrap">
                    {info.company_logo ? (
                        <img src={info.company_logo} alt="" className="h-12 w-auto" />
                    ) : (
                        <div className="h-12 w-12 flex items-center justify-center font-display font-bold text-white" style={{ background: accent }}>
                            {(info.company_name || "H")[0]}
                        </div>
                    )}
                    <div>
                        <div className="overline">{info.company_name || "HR Portal"}</div>
                        <h1 className="font-display font-black tracking-tighter text-3xl sm:text-4xl text-ink leading-[1.05] mt-1">
                            Document upload
                        </h1>
                    </div>
                </header>

                <div className="bg-white border border-ink/10 p-6 md:p-8 mb-6" data-testid="upload-info-card">
                    <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                        <div>
                            <div className="overline">For</div>
                            <div className="text-lg font-medium text-ink mt-1">
                                {info.employee_name}
                                {info.employee_no && <span className="text-ink/40 ml-2 text-sm">· {info.employee_no}</span>}
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="overline flex items-center gap-1.5 justify-end" style={{ color: accent }}>
                                <Clock className="w-3 h-3" /> Link expires
                            </div>
                            <div className="mt-1 text-sm font-medium text-ink tabular-nums" data-testid="upload-expires-label">
                                {timeUntil(info.expires_at)}
                            </div>
                        </div>
                    </div>
                    {info.note_to_employee && (
                        <div className="bg-bone p-3 text-sm text-ink/70 border-l-4 border-klein">
                            <strong className="text-ink">Note from {info.requested_by}:</strong> {info.note_to_employee}
                        </div>
                    )}
                    <p className="text-xs text-ink/50 mt-4">
                        Upload PDF files only · 5 MB max per file · Each item below can be uploaded once. Re-upload will replace.
                    </p>
                </div>

                {allDone && (
                    <div className="bg-emerald-50 border border-emerald-200 p-5 mb-6 flex items-start gap-3" data-testid="upload-all-done">
                        <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0 mt-0.5" />
                        <div>
                            <div className="font-medium text-emerald-900">All requested documents uploaded</div>
                            <div className="text-sm text-emerald-800 mt-1">HR has been notified. You can close this page now.</div>
                        </div>
                    </div>
                )}

                <div className="space-y-4">
                    {(info.doc_types || []).map((t) => (
                        <DocCard key={t.id} type={t} token={token} accent={accent} onUploaded={refreshInfo} />
                    ))}
                </div>

                <p className="text-xs text-ink/40 mt-8 text-center">
                    Powered by {info.company_name || "HRM"} · Secure upload portal
                </p>
            </div>
        </div>
    );
}

function CenterShell({ children }) {
    return (
        <div className="min-h-screen bg-bone flex items-center justify-center px-4">
            {children}
        </div>
    );
}

function DocCard({ type, token, accent, onUploaded }) {
    const inputRef = useRef(null);
    const [file, setFile] = useState(null);
    const [issuedDate, setIssuedDate] = useState("");
    const [expiryDate, setExpiryDate] = useState("");
    const [annualRent, setAnnualRent] = useState("");
    const [uploading, setUploading] = useState(false);
    const [done, setDone] = useState(type.uploaded);
    const isRent = type.id === "rent_receipt";

    const onFile = (f) => {
        if (!f) return;
        if (!f.name.toLowerCase().endsWith(".pdf")) {
            toast.error("Only PDF files are accepted");
            return;
        }
        if (f.size > 5 * 1024 * 1024) {
            toast.error("File too large (max 5 MB)");
            return;
        }
        setFile(f);
    };

    const onUpload = async () => {
        if (!file) {
            toast.error("Please select a PDF file first");
            return;
        }
        if (isRent) {
            if (!expiryDate) {
                toast.error("Rent receipt requires an expiry date");
                return;
            }
            if (!annualRent || Number(annualRent) <= 0) {
                toast.error("Enter the annual rent amount on the receipt — it sets your PAYE tax relief");
                return;
            }
        } else if (type.has_expiry && !expiryDate) {
            const proceed = window.confirm(`This document type usually has an expiry date. Continue without one?`);
            if (!proceed) return;
        }
        setUploading(true);
        try {
            const form = new FormData();
            form.append("file", file);
            const paramsObj = {
                doc_type: type.id,
                issued_date: issuedDate,
                expiry_date: expiryDate,
            };
            if (isRent) paramsObj.annual_rent = String(Number(annualRent));
            const params = new URLSearchParams(paramsObj);
            await PUBLIC_API.post(
                `/upload-token/${token}/upload?${params.toString()}`,
                form,
                { headers: { "Content-Type": "multipart/form-data" } }
            );
            setDone(true);
            toast.success(isRent
                ? "Rent receipt uploaded — awaiting Accountant approval before PAYE relief activates."
                : `${type.label} uploaded`);
            onUploaded?.();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    return (
        <div
            className={`bg-white border p-5 md:p-6 transition-colors ${done ? "border-emerald-200 bg-emerald-50/30" : "border-ink/15"}`}
            data-testid={`doc-card-${type.id}`}
        >
            <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center shrink-0" style={{ background: done ? "#10B981" : accent, color: "#fff" }}>
                        {done ? <CheckCircle2 className="w-5 h-5" /> : <FileText className="w-5 h-5" />}
                    </div>
                    <div>
                        <div className="font-medium text-ink">{type.label}</div>
                        {type.has_expiry && <div className="text-xs text-ink/50 mt-0.5">Has expiry date</div>}
                    </div>
                </div>
                {done && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 border border-emerald-300 text-emerald-800 text-[10px] uppercase tracking-[0.15em]">
                        <CheckCircle2 className="w-3 h-3" /> Uploaded
                    </span>
                )}
            </div>

            <input
                ref={inputRef}
                type="file"
                accept=".pdf,application/pdf"
                onChange={(e) => onFile(e.target.files?.[0])}
                className="hidden"
                data-testid={`doc-file-${type.id}`}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    disabled={uploading}
                    className="border border-dashed border-ink/30 px-4 py-3 text-sm hover:bg-bone text-left flex items-center gap-3 disabled:opacity-50"
                    data-testid={`doc-pick-${type.id}`}
                >
                    <Upload className="w-4 h-4 text-ink/60" />
                    <span className="text-ink/80 truncate flex-1">
                        {file ? file.name : "Choose PDF…"}
                    </span>
                    {file && <span className="text-xs text-ink/40">{formatBytes(file.size)}</span>}
                </button>

                {type.has_expiry && (
                    <div className="grid grid-cols-2 gap-2">
                        <label className="block">
                            <span className="text-[10px] uppercase tracking-[0.15em] text-ink/50 block mb-1">Issued</span>
                            <input
                                type="date"
                                value={issuedDate}
                                onChange={(e) => setIssuedDate(e.target.value)}
                                className="w-full border border-ink/15 px-2 py-2 text-sm focus:border-klein focus:outline-none"
                                data-testid={`doc-issued-${type.id}`}
                            />
                        </label>
                        <label className="block">
                            <span className="text-[10px] uppercase tracking-[0.15em] text-ink/50 block mb-1 flex items-center gap-1">
                                <Calendar className="w-2.5 h-2.5" /> Expires
                            </span>
                            <input
                                type="date"
                                value={expiryDate}
                                onChange={(e) => setExpiryDate(e.target.value)}
                                className="w-full border border-ink/15 px-2 py-2 text-sm focus:border-klein focus:outline-none"
                                data-testid={`doc-expiry-${type.id}`}
                            />
                        </label>
                    </div>
                )}
            </div>

            {isRent && (
                <div className="mt-4">
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-[0.15em] text-ink/50 block mb-1">
                            Annual rent on receipt (₦) *
                        </span>
                        <MoneyInput
                            value={annualRent}
                            onChange={(e) => setAnnualRent(e.target.value)}
                            placeholder="e.g. 1,500,000"
                            className="w-full border border-ink/15 px-3 py-2.5 text-sm focus:border-klein focus:outline-none"
                            data-testid={`doc-annual-rent-${type.id}`}
                        />
                    </label>
                    <p className="text-[11px] text-ink/50 mt-1.5">
                        Sets your PAYE tax relief (the lower of 20% × rent or ₦500,000 per year). Goes for HR/Accountant approval before activating.
                    </p>
                </div>
            )}

            {file && (
                <div className="mt-4 flex justify-end">
                    <button
                        type="button"
                        onClick={onUpload}
                        disabled={uploading}
                        className="inline-flex items-center gap-2 text-white px-5 py-2.5 text-sm hover:opacity-90 disabled:opacity-60"
                        style={{ background: accent }}
                        data-testid={`doc-submit-${type.id}`}
                    >
                        <Upload className="w-4 h-4" />
                        {uploading ? "Uploading…" : done ? "Replace" : "Upload"}
                    </button>
                </div>
            )}
        </div>
    );
}
